import type { AuthUser } from "@/features/auth/types";
import { normalizeAuthUser } from "@/features/auth/authUser";
import { tokenStorage } from "@/lib/http";

export const AUTH_USER_STORAGE_KEY = "rtf_user";
export const LAST_ACTIVITY_AT_STORAGE_KEY = "ridetofive-admin-last-activity-at";
export const AUTH_FORCE_LOGOUT_EVENT = "ridetofive-admin:force-logout";
export const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000;

export type ForcedLogoutReason = "unauthorized" | "forbidden" | "inactivity";

export type ForcedLogoutDetail = {
    reason: ForcedLogoutReason;
};

const isBrowser = (): boolean => typeof window !== "undefined";

export const readStoredUser = (): AuthUser | null => {
    if (!isBrowser()) {
        return null;
    }

    try {
        const stored = window.localStorage.getItem(AUTH_USER_STORAGE_KEY);
        if (!stored) {
            return null;
        }

        const parsed: unknown = JSON.parse(stored);
        return normalizeAuthUser(parsed as Partial<AuthUser>);
    } catch {
        return null;
    }
};

export const storeUser = (user: AuthUser): void => {
    if (!isBrowser()) {
        return;
    }

    const normalized = normalizeAuthUser(user);
    if (!normalized) {
        return;
    }

    window.localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(normalized));
};

export const touchSessionActivity = (timestamp = Date.now()): void => {
    if (!isBrowser()) {
        return;
    }

    window.localStorage.setItem(LAST_ACTIVITY_AT_STORAGE_KEY, String(timestamp));
};

export const readLastActivityAt = (): number | null => {
    if (!isBrowser()) {
        return null;
    }

    const raw = window.localStorage.getItem(LAST_ACTIVITY_AT_STORAGE_KEY);
    const parsed = raw ? Number(raw) : Number.NaN;
    return Number.isFinite(parsed) ? parsed : null;
};

export const clearSessionStorage = (): void => {
    tokenStorage.clearTokens();

    if (!isBrowser()) {
        return;
    }

    window.localStorage.removeItem(AUTH_USER_STORAGE_KEY);
    window.localStorage.removeItem(LAST_ACTIVITY_AT_STORAGE_KEY);
};

export const dispatchForcedLogout = (reason: ForcedLogoutReason): void => {
    if (!isBrowser()) {
        return;
    }

    window.dispatchEvent(
        new CustomEvent<ForcedLogoutDetail>(AUTH_FORCE_LOGOUT_EVENT, {
            detail: { reason }
        })
    );
};

export const redirectToLogin = (): void => {
    if (!isBrowser()) {
        return;
    }

    if (window.location.pathname !== "/auth/login") {
        window.location.assign("/auth/login");
    }
};
